import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import type { 
  ReleaseDto,
  CreateReleaseDto,
  UpdateReleaseDto,
  ReleaseActionDto,
  ReleaseNodePipelineRunDto,
  CreateReleaseNodePipelineRunDto,
  ReleasesPaginatedResponse,
  ReleasesQueryParams
} from '../types/release';
import type { PipelineRunDto } from '../types/pipelineFlow';

export interface UseReleasesOptions extends ReleasesQueryParams {}

export const useReleases = (options?: UseReleasesOptions) => {
  return useQuery<ReleasesPaginatedResponse>({
    queryKey: ['releases', options],
    queryFn: () => apiClient.getReleases(options),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRelease = (id: string | undefined) => {
  return useQuery<ReleaseDto>({
    queryKey: ['release', id],
    queryFn: () => apiClient.getRelease(id!),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useCreateRelease = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseDto, Error, CreateReleaseDto>({
    mutationFn: apiClient.createRelease,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
    onError: (error: any) => {
      // Log the full error for debugging
      console.error('Release creation error:', error);
      
      // The error message will be handled by the component using this hook
      // We just ensure the error is properly propagated
    },
  });
};

export const useUpdateRelease = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseDto, Error, { id: string; data: UpdateReleaseDto }>({
    mutationFn: ({ id, data }) => apiClient.updateRelease(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release', data.id] });
    },
  });
};

export const useDeleteRelease = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: apiClient.deleteRelease,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });
};

export const useActivateRelease = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseDto, Error, { id: string; data: ReleaseActionDto }>({
    mutationFn: ({ id, data }) => apiClient.activateRelease(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release', variables.id] });
    },
  });
};

export const useArchiveRelease = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseDto, Error, { id: string; data: ReleaseActionDto }>({
    mutationFn: ({ id, data }) => apiClient.archiveRelease(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release', variables.id] });
    },
  });
};

export const useMarkReleaseReady = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ReleaseDto, Error, { id: string; data: ReleaseActionDto }>({
    mutationFn: ({ id, data }) => apiClient.markReleaseReady(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release', variables.id] });
    },
  });
};

// Pipeline Run Selection Hooks
export const usePipelineRunsByPipelineId = (
  pipelineId: string | undefined,
  limit: number = 25,
  options?: {
    includeStages?: boolean;
    productionReadyOnly?: boolean;
  }
) => {
  const includeStages = options?.includeStages ?? false;
  const productionReadyOnly = options?.productionReadyOnly ?? false;
  
  return useQuery<PipelineRunDto[]>({
    queryKey: ['pipelineRuns', 'byPipelineId', pipelineId, limit, includeStages, productionReadyOnly],
    queryFn: () => apiClient.getPipelineRunsByPipelineId(pipelineId!, limit, includeStages, productionReadyOnly),
    enabled: !!pipelineId,
    staleTime: 2 * 60 * 1000, // 2 minutes - pipeline runs data changes more frequently
  });
};

export const useCreatePipelineRunSelection = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    ReleaseNodePipelineRunDto, 
    Error, 
    { releaseId: string; data: CreateReleaseNodePipelineRunDto }
  >({
    mutationFn: ({ releaseId, data }) => apiClient.createPipelineRunSelection(releaseId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['release', data.releaseId] });
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });
};

// Hook for managing release lifecycle
export const useReleaseLifecycle = () => {
  const activateMutation = useActivateRelease();
  const archiveMutation = useArchiveRelease();
  const markReadyMutation = useMarkReleaseReady();

  const activateRelease = async (id: string, actionBy: string) => {
    return activateMutation.mutateAsync({ id, data: { actionBy } });
  };

  const archiveRelease = async (id: string, actionBy: string) => {
    return archiveMutation.mutateAsync({ id, data: { actionBy } });
  };

  const markReleaseReady = async (id: string, actionBy: string) => {
    return markReadyMutation.mutateAsync({ id, data: { actionBy } });
  };

  return {
    activateRelease,
    archiveRelease,
    markReleaseReady,
    isUpdating: activateMutation.isPending || archiveMutation.isPending || markReadyMutation.isPending,
    error: activateMutation.error || archiveMutation.error || markReadyMutation.error,
  };
};

// Hook for managing release editor state
export const useReleaseEditor = () => {
  const createMutation = useCreateRelease();
  const updateMutation = useUpdateRelease();

  const saveRelease = async (
    data: CreateReleaseDto | (UpdateReleaseDto & { id: string })
  ) => {
    if ('id' in data) {
      const { id, ...updateData } = data;
      return updateMutation.mutateAsync({ id, data: updateData });
    } else {
      return createMutation.mutateAsync(data);
    }
  };

  return {
    saveRelease,
    isSaving: createMutation.isPending || updateMutation.isPending,
    error: createMutation.error || updateMutation.error,
  };
};